import { config } from "../utils/settings"

export const settings = {
    groundPlatformsOffset: -8, // ground platforms have a little ledge to them which I don't want included in the hitbox nor calculations
    groundHeight: 100,
    gapWidth: 100
}

const listPlatforms = [
    "ground_full","ground_c1","ground_c2","ground_c3",
    "float_black","float_white","float_wide_black","float_wide_white", 
    "gap",
] as const

const listCosmetics = [
    "plant1","plant2","plant3","rock1","rock2","rock3","feather1","feather2"
] as const

const listPickups = [
    "item_banana", "item_orange", "item_apple", "item_spoon", "item_milk", "item_cereal_1", "item_cereal_2"
] as const

const listSystems = [
    "respawnTrig", "recordStartTrig", "recordEndTrig", "killWallTrig", "invisWall", "boss"
] as const

const listHazards = [
    "trap_cube","trap_milk_start","trap_milk","trap_thorn_bush","trap_thorns","trap_knife"
] as const

const typeMap = {
    platform: listPlatforms,
    cosmetic: listCosmetics,
    pickups: listPickups,
    system: listSystems,
    hazard: listHazards
} as const;

export function returnPlatformType<K extends keyof typeof typeMap>(input: string): 
    | { type: K, value: (typeof typeMap)[K][number] }
    | { type: 'unknown' } {
    for (const [type, list] of Object.entries(typeMap)) {
        if ((list as readonly string[]).includes(input)) {
            return { type: type as K, value: input as (typeof typeMap)[K][number] };
        }
    }
    return { type: 'unknown' };
}

type typePlatforms = typeof listPlatforms[number];
type typeCosmetics = typeof listCosmetics[number];
type typePickups = typeof listPickups[number];
type typeSystems = typeof listSystems[number];
type typeHazards = typeof listHazards[number];

export interface platform {
    name: typePlatforms,
    height: number,
    force?: { x: number, relative: boolean },
    cosmetics?: { name: typeCosmetics, x: number }[],
    pickups?: { name: typePickups, x: number, y: number }[],
    systems?: { name: typeSystems, x: number, y: number, width: number, height: number }[],
}

export interface hazard {
    name: typeHazards,
    height: number,
    force?: { x: number, relative: boolean }
}

function genWaterfall(pos: {x: number, y: number, bottom: number}, waves: number = 1) {
    const milkBottom = pos.y-pos.bottom;
    const inset = 30;
    pos.x -= inset;

    let milkFall: hazard[] = []

    milkFall.push({ name: 'trap_milk_start', height: pos.y, force:{x: pos.x, relative: true}  })
    for(let i = 0; i < waves; i++) {
        milkFall.push({
            name: 'trap_milk', 
            height: milkBottom, 
            force:{
                x: pos.x + i*79, 
                relative: true
            }
        })
    }

    return milkFall;
}

// Render order is top down
// Traps should be rendered before the previously placed platform to ensure correct rendering (sadly)
// Render Layers are offered in PixiJS v8, maybe something to consider upgrading to

// Also I have to do this weird concat stuff I guess

export const tutorial01: (platform|hazard)[] = [
    { 
        name: 'ground_c1', height: 452, force: {x: config.width - 20, relative: true},
        systems: [
            { name: "killWallTrig", x: -40, y: -452, width: 50, height: 500 },
        ]
    },
    { 
        name: 'ground_full', height: 0,
        pickups: [
            { name: "item_banana", x: (config.width/2), y: 70 },
        ]
    },
    { name: 'ground_full', height: 0 }
]

export const tutorial02: (platform|hazard)[] = [
    { 
        name: 'ground_c1', height: 452, force: {x: config.width - 20, relative: true},
        systems: [
            { name: "killWallTrig", x: -40, y: -452, width: 50, height: 500 },
        ]
    },
    { 
        name: 'ground_full', height: 0,
        pickups: [
            { name: "item_cereal_1", x: 160, y: 180 },
        ]
    },
    { name: 'ground_full', height: 0, }
]

export const playground: (platform|hazard)[] = [
    { 
        name: 'ground_full', height: 0,
        force: {x: -456, relative: false},
    },
    { 
        name: 'ground_full', height: 0,
        force: {x: -656 + config.width - 50, relative: false},
        pickups: [
            { name: "item_cereal_2", x: 450, y: 70 },
            { name: "item_orange", x: 570, y: 200 },
        ]
    },
    { 
        name: 'float_black', height: 180, 
        force:{x: 130, relative: true},
        pickups: [
            { name: "item_apple", x: 80, y: 80 },
        ]
    },
    { 
        name: 'float_black', height: 120, 
        force:{x: config.width - 120, relative: true} 
    },
]

// ------------------------------------------------------------------
// --------------------------------------- [ Actual level building ] 
export const zone01: (platform|hazard)[] = 
([] as (platform|hazard)[]).concat(
    [
        { name: 'ground_c1', height: 452, force: {x: -115, relative: false} },
        { 
            name: 'ground_full', height: 0, force: {x: 528, relative: false},
            pickups: [
                { name: "item_banana", x: 110, y: 250 },
                { name: "item_apple", x: 480, y: 340 },
                { name: "item_orange", x: 580, y: 390 },
            ]
        }, { 
            name: 'ground_full', height: 100, 
            cosmetics: [
                { name: 'plant1', x: 35 },
                { name: 'rock1', x: 15 },
                { name: 'feather1', x: 180 },
                { name: 'feather2', x: 220 },
            ],
        },
        { name: 'float_black', height: 205, force:{x: 75, relative: true} },
        { name: 'float_black', height: 265, force:{x: 286, relative: true} },
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { 
            name: 'ground_c3', height: 200,
            pickups: [
                { name: "item_orange", x: 280, y: 180 },
            ], 
            // End recording
            // systems: [
            //     { name: "recordEndTrig", x: 180, y: 0, width: 50, height: 1000 }
            // ]
        },
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'ground_c3', height: 100 },        
        { name: 'ground_full', height: 0 },
        { 
            name: 'float_white', height: 200, force:{x: -558, relative: true},
            pickups: [
                { name: "item_orange", x: -80, y: 120 },
            ]
        }, { 
            name: 'float_black', height: 270, force:{x: -378, relative: true},
            pickups: [
                { name: "item_banana", x: -70, y: 100 },
            ]
        }, { 
            name: 'float_wide_white', height: 300, force:{x: -180, relative: true},
            pickups: [
                { name: "item_apple", x: -40, y: 120 },
            ]
        }, { 
            name: 'float_black', height: 150, force:{x: 80, relative: true},
        },
        { name: 'trap_cube', height: -40},
        { name: 'trap_cube', height: -40},
        { name: 'trap_cube', height: -40},
    ],

    // This forms part of the next zone
    genWaterfall({x: 257, y: 229, bottom: 220}, 16),
    [
        { 
            name: 'ground_c3', height: 329,
            pickups: [
                // { name: "item_apple", x: 80, y: 180 },
                { name: "item_cereal_1", x: 80, y: 180 },
            ],
            cosmetics: [
                { name: 'feather1', x: 20 },
                { name: 'feather2', x: 60 },
            ],
            // Respawn point!
            // Start recording
            systems: [
                { name: "recordStartTrig", x: 60, y: 0, width: 50, height: 1000 },
                { name: "respawnTrig", x: 20, y: 0, width: 50, height: 1000 }
            ]
        },
    ],

    // Gap zone
    { name: 'gap', height: 0},
    { name: 'gap', height: 0},
    { 
        name: 'float_wide_black', height: 200, 
        force:{x: -50, relative: true},
        pickups: [
            { name: "item_cereal_2", x: 50, y: 100 },
        ]
    },
    { 
        name: 'float_black', height: 340, 
        force:{x: 180, relative: true},
        pickups: [
            { name: "item_cereal_1", x: 50, y: 100 },
        ]
    },
    { name: 'gap', height: 0},
    { name: 'gap', height: 0},
    { name: 'gap', height: 0},
    { name: 'ground_c2', height: 59, force:{x: -120, relative: true} },
    { name: 'ground_c1', height: 109, force:{x: 0, relative: true} },
    { name: 'gap', height: 0},
    { 
        name: 'float_white', height: 450, 
        force:{x: 30, relative: true},
        pickups: [
            { name: "item_cereal_2", x: 50, y: 100 },
        ]
    },
    { 
        name: 'float_wide_black', height: 400, 
        force:{x: 350, relative: true},
        pickups: [
            { name: "item_cereal_1", x: 80, y: 100 },
        ]
    },
    { name: 'gap', height: 0},
    { name: 'gap', height: 0},
    { name: 'gap', height: 0},
    { name: 'gap', height: 0},
)

export const zone02: (platform|hazard)[] = 
([] as (platform|hazard)[]).concat(
    [
        // Brief respite
        { 
            name: 'ground_full', height: 75, force: {x: -120, relative: true},
        },
        {
            name: 'ground_full', height: 175,
            pickups: [
                { name: "item_cereal_2", x: 150, y: 80 },
                { name: "item_cereal_1", x: 200, y: 80 },
                { name: "item_cereal_2", x: 250, y: 80 },
                { name: "item_milk", x: 350, y: 80 },
            ],
            cosmetics: [
                { name: 'feather1', x: 460 },
                { name: 'feather2', x: 500 },
            ],
            // Respawn point!
            // End recording
            // Start recording
            systems: [
                { name: "respawnTrig", x: 460, y: 0, width: 50, height: 1500 },
                // { name: "recordEndTrig", x: 470, y: 0, width: 50, height: 1000 },
                { name: "recordStartTrig", x: 490, y: 0, width: 50, height: 1000 }
            ]
        },
        { name: 'trap_thorns', height: 60, force:{ x: -20, relative: true} },
        { name: 'trap_thorns', height: 20, force:{ x: 30, relative: true} },
        { name: 'trap_thorns', height: 0, force:{ x: 80, relative: true} },
        { name: 'trap_thorns', height: -10, force:{ x: 130, relative: true} },
        { name: 'trap_thorns', height: -20, force:{ x: 180, relative: true} },
        { name: 'trap_thorns', height: -20, force:{ x: 230, relative: true} },
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { 
            name: 'ground_c3', height: 0,
            pickups: [
                { name: "item_orange", x: 50, y: 80 },
            ],
        },
        { name: 'trap_thorns', height: -20, force:{ x: 0, relative: true} },
        { name: 'trap_thorns', height: 0, force:{ x: 50, relative: true} },
        { name: 'trap_thorns', height: 20, force:{ x: 100, relative: true} },
        { name: 'trap_thorns', height: 40, force:{ x: 150, relative: true} },
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        
        { name: 'float_wide_black', height: 200, force:{x: -180, relative: true} },
        { 
            name: 'ground_c2', height: 250,
            pickups: [
                { name: "item_banana", x: 50, y: 80 },
            ],
        },
        { name: 'trap_thorns', height: 10, force:{ x: 0, relative: true} },
        { name: 'trap_thorns', height: 10, force:{ x: 50, relative: true} },
        { name: 'trap_thorns', height: 0, force:{ x: 100, relative: true} },
        { name: 'trap_thorns', height: -15, force:{ x: 150, relative: true} },
        { name: 'trap_thorns', height: -25, force:{ x: 200, relative: true} },
        { 
            name: 'float_wide_black', height: 50, force:{x: -20, relative: true},
            pickups: [
                { name: "item_banana", x: 50, y: 80 },
            ], 
        },
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        
        { name: 'ground_c2', height: 0, force:{x: -320, relative: true} },
        { name: 'trap_thorns', height: 150, force:{ x: -50, relative: true} },
        { name: 'trap_thorns', height: 110, force:{ x: -100, relative: true} },
        { 
            name: 'float_wide_black', height: 200, 
            force:{x: -140, relative: true},
            pickups: [
                { name: "item_orange", x: 50, y: 80 },
            ], 
        },
        { 
            name: 'ground_c3', height: 250,
            cosmetics: [
                { name: 'feather1', x: 30 },
                { name: 'feather2', x: 70 },
            ],
            systems: [
                { name: "respawnTrig", x: 30, y: 0, width: 50, height: 1500 },
            ]
            
        },
        { name: 'trap_thorns', height: 200, force:{ x: 0, relative: true} },
        { name: 'trap_thorns', height: 190, force:{ x: 50, relative: true} },
        { name: 'gap', height: 0},
        { name: 'trap_thorns', height: 180, force:{ x: 0, relative: true} },
        { name: 'trap_thorns', height: 150, force:{ x: 50, relative: true} },
        { name: 'gap', height: 0},
        { 
            name: 'float_wide_black', height: 350, force:{x: -50, relative: true},
            pickups: [
                { name: "item_banana", x: 50, y: 80 },
            ], 
        },
        
        // Platform gaunlet
    ],
        // genWaterfall({x: 400, y: 60, bottom: 80}, 16),
    [
        { 
            name: 'ground_c3', height: 100, force:{ x: 150, relative: true},
            systems: [
                // End recording
                // Start recording
                // { name: "recordEndTrig", x: 0, y: 0, width: 50, height: 1500 },
                { name: "recordStartTrig", x: 60, y: 0, width: 50, height: 1500 },
            ]
        },
        { name: 'ground_c3', height: 200 },
        { 
            name: 'float_wide_white', height: 400, force:{x: 120, relative: true},
            pickups: [
                { name: "item_orange", x: 10, y: 80 },
                { name: "item_banana", x: 40, y: 80 },
                { name: "item_orange", x: 70, y: 80 },
                { name: "item_banana", x: 100, y: 80 },
                { name: "item_orange", x: 130, y: 80 },
            ], 
        },
        { 
            name: 'float_black', height: 300, force:{x: 420, relative: true},
            pickups: [
                // { name: "item_banana", x: 50, y: 80 },
            ], 
        },
        //Easy upper route
        { 
            name: 'float_wide_black', height: 400, force:{x: 620, relative: true},
            pickups: [
                { name: "item_banana", x: 50, y: 80 },
            ], 
        },
        { name: 'float_wide_white', height: 350, force:{x: 750, relative: true}, },
        { name: 'float_wide_black', height: 450, force:{x: 920, relative: true}, },
        // Lower route
        { 
            name: 'float_white', height: 20, force:{x: 320, relative: true},
            pickups: [
                { name: "item_milk", x: 50, y: 80 },
            ], 
        },
        { 
            name: 'float_white', height: 100, force:{x: 520, relative: true},
            pickups: [
                { name: "item_orange", x: 30, y: 80 },
                { name: "item_banana", x: 70, y: 80 },
            ], 
        },
        { 
            name: 'float_black', height: 50, force:{x: 620, relative: true},
            pickups: [
                { name: "item_banana", x: 50, y: 80 },
            ], 
        },
        { 
            name: 'float_white', height: 100, force:{x: 820, relative: true},
            pickups: [
                { name: "item_orange", x: 30, y: 80 },
                { name: "item_banana", x: 70, y: 80 },
            ], 
        },
        // End
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},
        { name: 'gap', height: 0},

    ]
)

export const zone03: (platform|hazard)[] = 
([] as (platform|hazard)[]).concat(
    [
        // { name: 'ground_full', height: 0, force: {x: 528, relative: false} },
        // { name: 'ground_full', height: 100 },
        // { name: 'float_black', height: 250, force:{x: 75, relative: true} },
        // { name: 'float_black', height: 250, force:{x: 286, relative: true} },
        // { name: 'gap', height: 0},
        // { name: 'gap', height: 0},
        // { name: 'gap', height: 0},
        // { name: 'gap', height: 0},
        // { name: 'gap', height: 0},
        // { name: 'ground_full', height: 100 },

        // { name: 'ground_c2', height: 229 },
        // { name: 'ground_full', height: 150 },
    ]
);

export const finalZone: (platform|hazard)[] =
    [
        { 
            name: 'ground_full', height: 150,
            pickups: [
                { name: "item_apple", x: 150, y: 80 },
                { name: "item_banana", x: 200, y: 80 },
                { name: "item_orange", x: 250, y: 80 },
                { name: "item_spoon", x: 350, y: 80 },
            ],
            cosmetics: [
                { name: 'feather1', x: 460 },
                { name: 'feather2', x: 500 },
            ]
        }, { 
            name: 'ground_full', height: 0,
            systems: [
                { name:"boss", x: 300, y: 0, width: 0, height: 0 }
            ]
        },
        { name: 'ground_c2', height: 329, force:{x: -100, relative: true} },
    ]
